import { edgeDevicesActionTypes } from "./edgeDevicesTypes";
import axios from "axios";
import axiosInstance from "../../utils/axiosInstance";
import baseUrl from "../../config/api";

// Base API URL for edge devices
const API_BASE = baseUrl + "/trackAPI/edge";

// Fetch clusters actions
export const fetchClustersStart = () => ({
  type: edgeDevicesActionTypes.FETCH_CLUSTERS_START
});

export const fetchClustersSuccess = (clusters) => ({
  type: edgeDevicesActionTypes.FETCH_CLUSTERS_SUCCESS,
  payload: clusters
});

export const fetchClustersFailure = (error) => ({
  type: edgeDevicesActionTypes.FETCH_CLUSTERS_FAILURE,
  payload: error
});

export const fetchClustersStartAsync = (userId, token) => {
  return async (dispatch) => {
    dispatch(fetchClustersStart());
    
    try {
      const response = await axiosInstance.get(`${API_BASE}/users/${userId}/clusters`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Handle various response formats gracefully
      const clusters = response.data?.clusters || response.data || [];
      
      // Ensure we always dispatch an array, even if empty
      dispatch(fetchClustersSuccess(Array.isArray(clusters) ? clusters : []));
    } catch (error) {
      // Handle different types of errors
      let errorMessage = 'Failed to fetch clusters';
      
      if (error.response) {
        // Server responded with error status
        const status = error.response.status;
        const serverMessage = error.response.data?.message || error.response.data?.error;
        
        if (status === 404) {
          // No clusters found - this is normal for new users
          dispatch(fetchClustersSuccess([]));
          return;
        } else if (status === 401) {
          errorMessage = 'Authentication failed. Please log in again.';
        } else if (status === 403) {
          errorMessage = 'Access denied. You do not have permission to view clusters.';
        } else if (status === 500) {
          errorMessage = 'Server error. Please try again later.';
        } else if (serverMessage) {
          errorMessage = serverMessage;
        }
      } else if (error.request) {
        // Network error
        errorMessage = 'Network error. Please check your connection and try again.';
      } else {
        // Other error
        errorMessage = error.message || errorMessage;
      }
      
      dispatch(fetchClustersFailure(errorMessage));
    }
  };
};

// Create cluster actions
export const createClusterStart = () => ({
  type: edgeDevicesActionTypes.CREATE_CLUSTER_START
});

export const createClusterSuccess = (cluster) => ({
  type: edgeDevicesActionTypes.CREATE_CLUSTER_SUCCESS,
  payload: cluster
});

export const createClusterFailure = (error) => ({
  type: edgeDevicesActionTypes.CREATE_CLUSTER_FAILURE,
  payload: error
});

export const createClusterStartAsync = (userId, token, clusterData) => {
  return async (dispatch) => {
    dispatch(createClusterStart());
    
    try {
      const response = await axiosInstance.post(`${API_BASE}/users/${userId}/clusters`, clusterData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      dispatch(createClusterSuccess(response.data));
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to create cluster';
      dispatch(createClusterFailure(errorMessage));
    }
  };
};

// Get cluster details actions
export const fetchClusterDetailsStart = () => ({
  type: edgeDevicesActionTypes.FETCH_CLUSTER_DETAILS_START
});

export const fetchClusterDetailsSuccess = (clusterDetails) => ({
  type: edgeDevicesActionTypes.FETCH_CLUSTER_DETAILS_SUCCESS,
  payload: clusterDetails
});

export const fetchClusterDetailsFailure = (error) => ({
  type: edgeDevicesActionTypes.FETCH_CLUSTER_DETAILS_FAILURE,
  payload: error
});

export const fetchClusterDetailsStartAsync = (userId, token, clusterId) => {
  return async (dispatch) => {
    dispatch(fetchClusterDetailsStart());
    
    try {
      const response = await axiosInstance.get(`${API_BASE}/users/${userId}/clusters/${clusterId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      dispatch(fetchClusterDetailsSuccess(response.data));
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to fetch cluster details';
      dispatch(fetchClusterDetailsFailure(errorMessage));
    }
  };
};

// Get cluster health actions
export const fetchClusterHealthStart = () => ({
  type: edgeDevicesActionTypes.FETCH_CLUSTER_HEALTH_START
});

export const fetchClusterHealthSuccess = (clusterId, healthData) => ({
  type: edgeDevicesActionTypes.FETCH_CLUSTER_HEALTH_SUCCESS,
  payload: { clusterId, healthData }
});

export const fetchClusterHealthFailure = (error) => ({
  type: edgeDevicesActionTypes.FETCH_CLUSTER_HEALTH_FAILURE,
  payload: error
});

export const fetchClusterHealthStartAsync = (userId, token, clusterId) => {
  return async (dispatch) => {
    dispatch(fetchClusterHealthStart());
    
    try {
      const response = await axiosInstance.get(`${API_BASE}/users/${userId}/clusters/${clusterId}/health`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      dispatch(fetchClusterHealthSuccess(clusterId, response.data));
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to fetch cluster health';
      dispatch(fetchClusterHealthFailure(errorMessage));
    }
  };
};

// Get service status actions
export const fetchServiceStatusStart = () => ({
  type: edgeDevicesActionTypes.FETCH_SERVICE_STATUS_START
});

export const fetchServiceStatusSuccess = (serviceStatus) => ({
  type: edgeDevicesActionTypes.FETCH_SERVICE_STATUS_SUCCESS,
  payload: serviceStatus
});

export const fetchServiceStatusFailure = (error) => ({
  type: edgeDevicesActionTypes.FETCH_SERVICE_STATUS_FAILURE,
  payload: error
});

export const fetchServiceStatusStartAsync = (userId, token) => {
  return async (dispatch) => {
    dispatch(fetchServiceStatusStart());
    
    try {
      const response = await axiosInstance.get(`${API_BASE}/users/${userId}/clusters/service/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Handle various response formats gracefully
      const serviceStatus = response.data || null;
      
      dispatch(fetchServiceStatusSuccess(serviceStatus));
    } catch (error) {
      // Create a structured error object that includes type and original error
      let errorObj = {
        type: 'UNKNOWN',
        message: 'Failed to fetch service status',
        originalError: error
      };
      
      if (error.response) {
        // Server responded with error status
        const status = error.response.status;
        const serverMessage = error.response.data?.message || error.response.data?.error;
        
        if (status === 404) {
          // No service status available - this is normal for new users
          dispatch(fetchServiceStatusSuccess(null));
          return;
        } else if (status === 401) {
          errorObj = {
            type: 'AUTH',
            message: 'Your session has expired. Please log in again.',
            originalError: error
          };
        } else if (status === 403) {
          // Handle forbidden specifically
          errorObj = {
            type: 'FORBIDDEN',
            message: 'You do not have permission to view service status.',
            originalError: error
          };
          
          // For 403 errors, instead of failing, set empty service status
          dispatch(fetchServiceStatusSuccess(null));
          return;
        } else if (status === 500) {
          errorObj = {
            type: 'SERVER',
            message: 'The server encountered an error. Please try again later.',
            originalError: error
          };
        } else if (serverMessage) {
          errorObj.message = serverMessage;
        }
      } else if (error.request) {
        // Network error
        errorObj = {
          type: 'NETWORK',
          message: 'Unable to connect to the server. Please check your network connection.',
          originalError: error
        };
      } else {
        // Other error
        errorObj.message = error.message || errorObj.message;
      }
      
      dispatch(fetchServiceStatusFailure(errorObj));
    }
  };
};

// Delete cluster actions
export const deleteClusterStart = () => ({
  type: edgeDevicesActionTypes.DELETE_CLUSTER_START
});

export const deleteClusterSuccess = (clusterId) => ({
  type: edgeDevicesActionTypes.DELETE_CLUSTER_SUCCESS,
  payload: clusterId
});

export const deleteClusterFailure = (error) => ({
  type: edgeDevicesActionTypes.DELETE_CLUSTER_FAILURE,
  payload: error
});

export const deleteClusterStartAsync = (userId, token, clusterId) => {
  return async (dispatch) => {
    dispatch(deleteClusterStart());
    
    try {
      await axiosInstance.delete(`${API_BASE}/users/${userId}/clusters/${clusterId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      dispatch(deleteClusterSuccess(clusterId));
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to delete cluster';
      dispatch(deleteClusterFailure(errorMessage));
    }
  };
};

// Update cluster status actions
export const updateClusterStatusStart = () => ({
  type: edgeDevicesActionTypes.UPDATE_CLUSTER_STATUS_START
});

export const updateClusterStatusSuccess = (clusterId, status) => ({
  type: edgeDevicesActionTypes.UPDATE_CLUSTER_STATUS_SUCCESS,
  payload: { clusterId, status }
});

export const updateClusterStatusFailure = (error) => ({
  type: edgeDevicesActionTypes.UPDATE_CLUSTER_STATUS_FAILURE,
  payload: error
});

export const updateClusterStatusStartAsync = (userId, token, clusterId, status) => {
  return async (dispatch) => {
    dispatch(updateClusterStatusStart());
    
    try {
      const response = await axiosInstance.patch(`${API_BASE}/users/${userId}/clusters/${clusterId}/status`, 
        { status }, 
        { headers: { Authorization: `Bearer ${token}` } }
      );
      dispatch(updateClusterStatusSuccess(clusterId, response.data.status));
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to update cluster status';
      dispatch(updateClusterStatusFailure(errorMessage));
    }
  };
};

// Fetch nodes actions
export const fetchNodesStart = () => ({
  type: edgeDevicesActionTypes.FETCH_NODES_START
});

export const fetchNodesSuccess = (clusterId, nodes) => ({
  type: edgeDevicesActionTypes.FETCH_NODES_SUCCESS,
  payload: { clusterId, nodes }
});

export const fetchNodesFailure = (error) => ({
  type: edgeDevicesActionTypes.FETCH_NODES_FAILURE,
  payload: error
});

export const fetchNodesStartAsync = (userId, token, clusterId) => {
  return async (dispatch) => {
    dispatch(fetchNodesStart());
    
    try {
      const response = await axiosInstance.get(`${API_BASE}/users/${userId}/clusters/${clusterId}/nodes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      dispatch(fetchNodesSuccess(clusterId, response.data));
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to fetch nodes';
      dispatch(fetchNodesFailure(errorMessage));
    }
  };
};

// Create node manually actions
export const createNodeStart = () => ({
  type: edgeDevicesActionTypes.CREATE_NODE_START
});

export const createNodeSuccess = (clusterId, node) => ({
  type: edgeDevicesActionTypes.CREATE_NODE_SUCCESS,
  payload: { clusterId, node }
});

export const createNodeFailure = (error) => ({
  type: edgeDevicesActionTypes.CREATE_NODE_FAILURE,
  payload: error
});

export const createNodeStartAsync = (userId, token, clusterId, nodeData) => {
  return async (dispatch) => {
    dispatch(createNodeStart());
    
    try {
      const response = await axiosInstance.post(`${API_BASE}/users/${userId}/clusters/${clusterId}/nodes`, nodeData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      dispatch(createNodeSuccess(clusterId, response.data));
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to create node';
      dispatch(createNodeFailure(errorMessage));
    }
  };
};

// Get node telemetry actions
export const fetchNodeTelemetryStart = () => ({
  type: edgeDevicesActionTypes.FETCH_NODE_TELEMETRY_START
});

export const fetchNodeTelemetrySuccess = (nodeId, telemetryData) => ({
  type: edgeDevicesActionTypes.FETCH_NODE_TELEMETRY_SUCCESS,
  payload: { nodeId, telemetryData }
});

export const fetchNodeTelemetryFailure = (error) => ({
  type: edgeDevicesActionTypes.FETCH_NODE_TELEMETRY_FAILURE,
  payload: error
});

export const fetchNodeTelemetryStartAsync = (userId, token, clusterId, nodeId) => {
  return async (dispatch) => {
    dispatch(fetchNodeTelemetryStart());
    
    try {
      const response = await axiosInstance.get(`${API_BASE}/users/${userId}/clusters/${clusterId}/nodes/${nodeId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      dispatch(fetchNodeTelemetrySuccess(nodeId, response.data));
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to fetch node telemetry';
      dispatch(fetchNodeTelemetryFailure(errorMessage));
    }
  };
};

// Update node status actions
export const updateNodeStatusStart = () => ({
  type: edgeDevicesActionTypes.UPDATE_NODE_STATUS_START
});

export const updateNodeStatusSuccess = (clusterId, nodeId, status) => ({
  type: edgeDevicesActionTypes.UPDATE_NODE_STATUS_SUCCESS,
  payload: { clusterId, nodeId, status }
});

export const updateNodeStatusFailure = (error) => ({
  type: edgeDevicesActionTypes.UPDATE_NODE_STATUS_FAILURE,
  payload: error
});

export const updateNodeStatusStartAsync = (userId, token, clusterId, nodeId, status) => {
  return async (dispatch) => {
    dispatch(updateNodeStatusStart());
    
    try {
      const response = await axiosInstance.patch(`${API_BASE}/users/${userId}/clusters/${clusterId}/nodes/${nodeId}/status`, 
        { status }, 
        { headers: { Authorization: `Bearer ${token}` } }
      );
      dispatch(updateNodeStatusSuccess(clusterId, nodeId, response.data.status));
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to update node status';
      dispatch(updateNodeStatusFailure(errorMessage));
    }
  };
};

// Delete node actions
export const deleteNodeStart = () => ({
  type: edgeDevicesActionTypes.DELETE_NODE_START
});

export const deleteNodeSuccess = (clusterId, nodeId) => ({
  type: edgeDevicesActionTypes.DELETE_NODE_SUCCESS,
  payload: { clusterId, nodeId }
});

export const deleteNodeFailure = (error) => ({
  type: edgeDevicesActionTypes.DELETE_NODE_FAILURE,
  payload: error
});

export const deleteNodeStartAsync = (userId, token, clusterId, nodeId) => {
  return async (dispatch) => {
    dispatch(deleteNodeStart());
    
    try {
      await axiosInstance.delete(`${API_BASE}/users/${userId}/clusters/${clusterId}/nodes/${nodeId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      dispatch(deleteNodeSuccess(clusterId, nodeId));
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to delete node';
      dispatch(deleteNodeFailure(errorMessage));
    }
  };
};

// Assign node to cluster actions
export const assignNodeStart = () => ({
  type: edgeDevicesActionTypes.ASSIGN_NODE_START
});

export const assignNodeSuccess = (clusterId, node) => ({
  type: edgeDevicesActionTypes.ASSIGN_NODE_SUCCESS,
  payload: { clusterId, node }
});

export const assignNodeFailure = (error) => ({
  type: edgeDevicesActionTypes.ASSIGN_NODE_FAILURE,
  payload: error
});

export const assignNodeStartAsync = (userId, token, clusterId, nodeId) => {
  return async (dispatch) => {
    dispatch(assignNodeStart());
    
    try {
      const response = await axiosInstance.post(`${API_BASE}/users/${userId}/clusters/${clusterId}/nodes/assign`, 
        { nodeId }, 
        { headers: { Authorization: `Bearer ${token}` } }
      );
      dispatch(assignNodeSuccess(clusterId, response.data));
      // Refresh unassigned nodes list
      dispatch(fetchUnassignedNodesStartAsync(userId, token));
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to assign node';
      dispatch(assignNodeFailure(errorMessage));
    }
  };
};

// Remove node from cluster actions
export const removeNodeStart = () => ({
  type: edgeDevicesActionTypes.REMOVE_NODE_START
});

export const removeNodeSuccess = (clusterId, nodeId, node) => ({
  type: edgeDevicesActionTypes.REMOVE_NODE_SUCCESS,
  payload: { clusterId, nodeId, node }
});

export const removeNodeFailure = (error) => ({
  type: edgeDevicesActionTypes.REMOVE_NODE_FAILURE,
  payload: error
});

export const removeNodeStartAsync = (userId, token, clusterId, nodeId) => {
  return async (dispatch) => {
    dispatch(removeNodeStart());
    
    try {
      const response = await axiosInstance.post(`${API_BASE}/users/${userId}/clusters/${clusterId}/nodes/${nodeId}/remove`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      dispatch(removeNodeSuccess(clusterId, nodeId, response.data));
      // Refresh unassigned nodes list
      dispatch(fetchUnassignedNodesStartAsync(userId, token));
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to remove node';
      dispatch(removeNodeFailure(errorMessage));
    }
  };
};

// Transfer node between clusters actions
export const transferNodeStart = () => ({
  type: edgeDevicesActionTypes.TRANSFER_NODE_START
});

export const transferNodeSuccess = (sourceClusterId, targetClusterId, node) => ({
  type: edgeDevicesActionTypes.TRANSFER_NODE_SUCCESS,
  payload: { sourceClusterId, targetClusterId, node }
});

export const transferNodeFailure = (error) => ({
  type: edgeDevicesActionTypes.TRANSFER_NODE_FAILURE,
  payload: error
});

export const transferNodeStartAsync = (userId, token, sourceClusterId, targetClusterId, nodeId) => {
  return async (dispatch) => {
    dispatch(transferNodeStart());
    
    try {
      const response = await axiosInstance.post(`${API_BASE}/users/${userId}/clusters/${sourceClusterId}/nodes/${nodeId}/transfer`, 
        { targetClusterId }, 
        { headers: { Authorization: `Bearer ${token}` } }
      );
      dispatch(transferNodeSuccess(sourceClusterId, targetClusterId, response.data));
      // Refresh both cluster nodes
      dispatch(fetchNodesStartAsync(userId, token, sourceClusterId));
      dispatch(fetchNodesStartAsync(userId, token, targetClusterId));
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to transfer node';
      dispatch(transferNodeFailure(errorMessage));
    }
  };
};

// Fetch unassigned nodes actions
export const fetchUnassignedNodesStart = () => ({
  type: edgeDevicesActionTypes.FETCH_UNASSIGNED_NODES_START
});

export const fetchUnassignedNodesSuccess = (nodes) => ({
  type: edgeDevicesActionTypes.FETCH_UNASSIGNED_NODES_SUCCESS,
  payload: nodes
});

export const fetchUnassignedNodesFailure = (error) => ({
  type: edgeDevicesActionTypes.FETCH_UNASSIGNED_NODES_FAILURE,
  payload: error
});

export const fetchUnassignedNodesStartAsync = (userId, token) => {
  return async (dispatch) => {
    dispatch(fetchUnassignedNodesStart());
    
    try {
      const response = await axiosInstance.get(`${API_BASE}/users/${userId}/nodes/unassigned`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Handle various response formats gracefully
      const nodes = response.data?.nodes || response.data || [];
      
      // Ensure we always dispatch an array, even if empty
      dispatch(fetchUnassignedNodesSuccess(Array.isArray(nodes) ? nodes : []));
    } catch (error) {
      // Create a structured error object that includes type and original error
      let errorObj = {
        type: 'UNKNOWN',
        message: 'Failed to fetch unassigned nodes',
        originalError: error
      };
      
      if (error.response) {
        // Server responded with error status
        const status = error.response.status;
        const serverMessage = error.response.data?.message || error.response.data?.error;
        
        if (status === 404) {
          // No unassigned nodes found - this is normal
          dispatch(fetchUnassignedNodesSuccess([]));
          return;
        } else if (status === 401) {
          errorObj = {
            type: 'AUTH',
            message: 'Your session has expired. Please log in again.',
            originalError: error
          };
        } else if (status === 403) {
          // Handle forbidden specifically
          errorObj = {
            type: 'FORBIDDEN',
            message: 'You do not have permission to view unassigned nodes.',
            originalError: error
          };
          
          // For 403 errors, instead of failing, set empty nodes
          dispatch(fetchUnassignedNodesSuccess([]));
          return;
        } else if (status === 500) {
          errorObj = {
            type: 'SERVER',
            message: 'The server encountered an error. Please try again later.',
            originalError: error
          };
        } else if (serverMessage) {
          errorObj.message = serverMessage;
        }
      } else if (error.request) {
        // Network error
        errorObj = {
          type: 'NETWORK',
          message: 'Unable to connect to the server. Please check your network connection.',
          originalError: error
        };
      } else {
        // Other error
        errorObj.message = error.message || errorObj.message;
      }
      
      dispatch(fetchUnassignedNodesFailure(errorObj));
    }
  };
};

// Fetch applications actions
export const fetchAppsStart = () => ({
  type: edgeDevicesActionTypes.FETCH_APPS_START
});

export const fetchAppsSuccess = (clusterId, apps) => ({
  type: edgeDevicesActionTypes.FETCH_APPS_SUCCESS,
  payload: { clusterId, apps }
});

export const fetchAppsFailure = (error) => ({
  type: edgeDevicesActionTypes.FETCH_APPS_FAILURE,
  payload: error
});

export const fetchAppsStartAsync = (userId, token, clusterId) => {
  return async (dispatch) => {
    dispatch(fetchAppsStart());
    
    try {
      const response = await axiosInstance.get(`${API_BASE}/users/${userId}/clusters/${clusterId}/apps`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      dispatch(fetchAppsSuccess(clusterId, response.data));
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to fetch applications';
      dispatch(fetchAppsFailure(errorMessage));
    }
  };
};

// Install application actions
export const installAppStart = () => ({
  type: edgeDevicesActionTypes.INSTALL_APP_START
});

export const installAppSuccess = (clusterId, appDetails) => ({
  type: edgeDevicesActionTypes.INSTALL_APP_SUCCESS,
  payload: { clusterId, appDetails }
});

export const installAppFailure = (error) => ({
  type: edgeDevicesActionTypes.INSTALL_APP_FAILURE,
  payload: error
});

export const installAppStartAsync = (userId, token, clusterId, appData) => {
  return async (dispatch) => {
    dispatch(installAppStart());
    
    try {
      const response = await axiosInstance.post(`${API_BASE}/users/${userId}/clusters/${clusterId}/apps`, appData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      dispatch(installAppSuccess(clusterId, response.data));
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to install application';
      dispatch(installAppFailure(errorMessage));
    }
  };
};

// Get application details actions
export const fetchAppDetailsStart = () => ({
  type: edgeDevicesActionTypes.FETCH_APP_DETAILS_START
});

export const fetchAppDetailsSuccess = (appId, appDetails) => ({
  type: edgeDevicesActionTypes.FETCH_APP_DETAILS_SUCCESS,
  payload: { appId, appDetails }
});

export const fetchAppDetailsFailure = (error) => ({
  type: edgeDevicesActionTypes.FETCH_APP_DETAILS_FAILURE,
  payload: error
});

export const fetchAppDetailsStartAsync = (userId, token, clusterId, appId) => {
  return async (dispatch) => {
    dispatch(fetchAppDetailsStart());
    
    try {
      const response = await axiosInstance.get(`${API_BASE}/users/${userId}/clusters/${clusterId}/apps/${appId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      dispatch(fetchAppDetailsSuccess(appId, response.data));
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to fetch application details';
      dispatch(fetchAppDetailsFailure(errorMessage));
    }
  };
};

// Update application status actions
export const updateAppStatusStart = () => ({
  type: edgeDevicesActionTypes.UPDATE_APP_STATUS_START
});

export const updateAppStatusSuccess = (clusterId, appId, status) => ({
  type: edgeDevicesActionTypes.UPDATE_APP_STATUS_SUCCESS,
  payload: { clusterId, appId, status }
});

export const updateAppStatusFailure = (error) => ({
  type: edgeDevicesActionTypes.UPDATE_APP_STATUS_FAILURE,
  payload: error
});

export const updateAppStatusStartAsync = (userId, token, clusterId, appId, status) => {
  return async (dispatch) => {
    dispatch(updateAppStatusStart());
    
    try {
      const response = await axiosInstance.patch(`${API_BASE}/users/${userId}/clusters/${clusterId}/apps/${appId}/status`, 
        { status }, 
        { headers: { Authorization: `Bearer ${token}` } }
      );
      dispatch(updateAppStatusSuccess(clusterId, appId, response.data.status));
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to update application status';
      dispatch(updateAppStatusFailure(errorMessage));
    }
  };
};

// Delete application actions
export const deleteAppStart = () => ({
  type: edgeDevicesActionTypes.DELETE_APP_START
});

export const deleteAppSuccess = (clusterId, appId) => ({
  type: edgeDevicesActionTypes.DELETE_APP_SUCCESS,
  payload: { clusterId, appId }
});

export const deleteAppFailure = (error) => ({
  type: edgeDevicesActionTypes.DELETE_APP_FAILURE,
  payload: error
});

export const deleteAppStartAsync = (userId, token, clusterId, appId) => {
  return async (dispatch) => {
    dispatch(deleteAppStart());
    
    try {
      await axiosInstance.delete(`${API_BASE}/users/${userId}/clusters/${clusterId}/apps/${appId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      dispatch(deleteAppSuccess(clusterId, appId));
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to delete application';
      dispatch(deleteAppFailure(errorMessage));
    }
  };
};

// Scale application actions
export const scaleAppStart = () => ({
  type: edgeDevicesActionTypes.SCALE_APP_START
});

export const scaleAppSuccess = (clusterId, appId, replicas) => ({
  type: edgeDevicesActionTypes.SCALE_APP_SUCCESS,
  payload: { clusterId, appId, replicas }
});

export const scaleAppFailure = (error) => ({
  type: edgeDevicesActionTypes.SCALE_APP_FAILURE,
  payload: error
});

export const scaleAppStartAsync = (userId, token, clusterId, appId, replicas) => {
  return async (dispatch) => {
    dispatch(scaleAppStart());
    
    try {
      const response = await axiosInstance.post(`${API_BASE}/users/${userId}/clusters/${clusterId}/apps/${appId}/scale`, 
        { replicas }, 
        { headers: { Authorization: `Bearer ${token}` } }
      );
      dispatch(scaleAppSuccess(clusterId, appId, response.data.replicas));
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to scale application';
      dispatch(scaleAppFailure(errorMessage));
    }
  };
};

// Restart application actions
export const restartAppStart = () => ({
  type: edgeDevicesActionTypes.RESTART_APP_START
});

export const restartAppSuccess = (clusterId, appId) => ({
  type: edgeDevicesActionTypes.RESTART_APP_SUCCESS,
  payload: { clusterId, appId }
});

export const restartAppFailure = (error) => ({
  type: edgeDevicesActionTypes.RESTART_APP_FAILURE,
  payload: error
});

export const restartAppStartAsync = (userId, token, clusterId, appId) => {
  return async (dispatch) => {
    dispatch(restartAppStart());
    
    try {
      await axiosInstance.post(`${API_BASE}/users/${userId}/clusters/${clusterId}/apps/${appId}/restart`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      dispatch(restartAppSuccess(clusterId, appId));
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to restart application';
      dispatch(restartAppFailure(errorMessage));
    }
  };
};

// Fetch services actions
export const fetchServicesStart = () => ({
  type: edgeDevicesActionTypes.FETCH_SERVICES_START
});

export const fetchServicesSuccess = (clusterId, services) => ({
  type: edgeDevicesActionTypes.FETCH_SERVICES_SUCCESS,
  payload: { clusterId, services }
});

export const fetchServicesFailure = (error) => ({
  type: edgeDevicesActionTypes.FETCH_SERVICES_FAILURE,
  payload: error
});

export const fetchServicesStartAsync = (userId, token, clusterId) => {
  return async (dispatch) => {
    dispatch(fetchServicesStart());
    
    try {
      // For now, services are part of cluster details
      // In the future, there might be a dedicated endpoint
      const response = await axiosInstance.get(`${API_BASE}/users/${userId}/clusters/${clusterId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const services = response.data.kubernetes?.services || [];
      dispatch(fetchServicesSuccess(clusterId, services));
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to fetch services';
      dispatch(fetchServicesFailure(errorMessage));
    }
  };
};

// Fetch pods actions
export const fetchPodsStart = () => ({
  type: edgeDevicesActionTypes.FETCH_PODS_START
});

export const fetchPodsSuccess = (clusterId, pods) => ({
  type: edgeDevicesActionTypes.FETCH_PODS_SUCCESS,
  payload: { clusterId, pods }
});

export const fetchPodsFailure = (error) => ({
  type: edgeDevicesActionTypes.FETCH_PODS_FAILURE,
  payload: error
});

export const fetchPodsStartAsync = (userId, token, clusterId) => {
  return async (dispatch) => {
    dispatch(fetchPodsStart());
    
    try {
      // For now, pods are part of cluster details
      // In the future, there might be a dedicated endpoint
      const response = await axiosInstance.get(`${API_BASE}/users/${userId}/clusters/${clusterId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const pods = response.data.kubernetes?.pods || [];
      dispatch(fetchPodsSuccess(clusterId, pods));
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to fetch pods';
      dispatch(fetchPodsFailure(errorMessage));
    }
  };
};

// Terminal command operations
export const executeTerminalCommandStart = () => ({
  type: edgeDevicesActionTypes.EXECUTE_TERMINAL_COMMAND_START
});

export const executeTerminalCommandSuccess = (result) => ({
  type: edgeDevicesActionTypes.EXECUTE_TERMINAL_COMMAND_SUCCESS,
  payload: result
});

export const executeTerminalCommandFailure = (error) => ({
  type: edgeDevicesActionTypes.EXECUTE_TERMINAL_COMMAND_FAILURE,
  payload: error
});

export const executeTerminalCommandStartAsync = (userId, token, clusterId, command) => {
  return async (dispatch) => {
    dispatch(executeTerminalCommandStart());
    
    try {
      // Mock terminal command execution for now
      // In a real implementation, this would connect to a WebSocket or execute kubectl commands
      const mockResult = {
        command,
        output: `Executing: ${command}\nCommand completed successfully.`,
        timestamp: new Date().toISOString()
      };
      
      // Add command to terminal output
      dispatch(addTerminalOutput({
        type: 'command',
        content: command,
        timestamp: new Date().toISOString()
      }));
      
      // Simulate command execution delay
      setTimeout(() => {
        dispatch(addTerminalOutput({
          type: 'output',
          content: mockResult.output,
          timestamp: mockResult.timestamp
        }));
        dispatch(executeTerminalCommandSuccess(mockResult));
      }, 500);
      
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to execute command';
      dispatch(executeTerminalCommandFailure(errorMessage));
      dispatch(addTerminalOutput({
        type: 'error',
        content: errorMessage,
        timestamp: new Date().toISOString()
      }));
    }
  };
};

// Set active cluster (for UI state)
export const setActiveCluster = (clusterId) => ({
  type: edgeDevicesActionTypes.SET_ACTIVE_CLUSTER,
  payload: clusterId
});

export const setActiveNamespace = (namespace) => ({
  type: edgeDevicesActionTypes.SET_ACTIVE_NAMESPACE,
  payload: namespace
});

// Terminal connection
export const connectTerminalStart = () => ({
  type: edgeDevicesActionTypes.CONNECT_TERMINAL_START
});

export const connectTerminalSuccess = (clusterId) => ({
  type: edgeDevicesActionTypes.CONNECT_TERMINAL_SUCCESS,
  payload: clusterId
});

export const connectTerminalFailure = (error) => ({
  type: edgeDevicesActionTypes.CONNECT_TERMINAL_FAILURE,
  payload: error
});

export const disconnectTerminal = () => ({
  type: edgeDevicesActionTypes.DISCONNECT_TERMINAL
});

export const connectTerminalStartAsync = (userId, token, clusterId) => {
  return async (dispatch) => {
    dispatch(connectTerminalStart());
    
    try {
      // Mock terminal connection for now
      // In a real implementation, this would establish a WebSocket connection
      setTimeout(() => {
        dispatch(connectTerminalSuccess(clusterId));
        dispatch(addTerminalOutput({
          type: 'system',
          content: `Connected to cluster: ${clusterId}`,
          timestamp: new Date().toISOString()
        }));
      }, 1000);
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to connect to terminal';
      dispatch(connectTerminalFailure(errorMessage));
    }
  };
};

// Clear terminal
export const clearTerminalOutput = () => ({
  type: edgeDevicesActionTypes.CLEAR_TERMINAL_OUTPUT
});

export const addTerminalOutput = (output) => ({
  type: edgeDevicesActionTypes.ADD_TERMINAL_OUTPUT,
  payload: output
});

// RBAC Actions - Users
export const fetchRbacUsersStart = () => ({
  type: edgeDevicesActionTypes.FETCH_RBAC_USERS_START
});

export const fetchRbacUsersSuccess = (users) => ({
  type: edgeDevicesActionTypes.FETCH_RBAC_USERS_SUCCESS,
  payload: users
});

export const fetchRbacUsersFailure = (error) => ({
  type: edgeDevicesActionTypes.FETCH_RBAC_USERS_FAILURE,
  payload: error
});

export const fetchRbacUsersAsync = (userId, token, clusterId) => {
  return async (dispatch) => {
    dispatch(fetchRbacUsersStart());
    
    try {
      const response = await axiosInstance.get(`${API_BASE}/users/${userId}/clusters/${clusterId}/rbac/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      dispatch(fetchRbacUsersSuccess(response.data));
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to fetch RBAC users';
      dispatch(fetchRbacUsersFailure(errorMessage));
    }
  };
};

// RBAC - Invite User
export const inviteUserStart = () => ({
  type: edgeDevicesActionTypes.INVITE_USER_START
});

export const inviteUserSuccess = (user) => ({
  type: edgeDevicesActionTypes.INVITE_USER_SUCCESS,
  payload: user
});

export const inviteUserFailure = (error) => ({
  type: edgeDevicesActionTypes.INVITE_USER_FAILURE,
  payload: error
});

export const inviteUserAsync = (userId, token, clusterId, userData) => {
  return async (dispatch) => {
    dispatch(inviteUserStart());
    
    try {
      const response = await axiosInstance.post(`${API_BASE}/users/${userId}/clusters/${clusterId}/rbac/users`, userData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      dispatch(inviteUserSuccess(response.data));
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to invite user';
      dispatch(inviteUserFailure(errorMessage));
    }
  };
};

// RBAC - Update User Role
export const updateUserRoleStart = () => ({
  type: edgeDevicesActionTypes.UPDATE_USER_ROLE_START
});

export const updateUserRoleSuccess = (updatedUser) => ({
  type: edgeDevicesActionTypes.UPDATE_USER_ROLE_SUCCESS,
  payload: updatedUser
});

export const updateUserRoleFailure = (error) => ({
  type: edgeDevicesActionTypes.UPDATE_USER_ROLE_FAILURE,
  payload: error
});

export const updateUserRoleAsync = (userId, token, clusterId, targetUserId, role) => {
  return async (dispatch) => {
    dispatch(updateUserRoleStart());
    
    try {
      const response = await axiosInstance.patch(`${API_BASE}/users/${userId}/clusters/${clusterId}/rbac/users/${targetUserId}`, 
        { role }, 
        { headers: { Authorization: `Bearer ${token}` } }
      );
      dispatch(updateUserRoleSuccess(response.data));
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to update user role';
      dispatch(updateUserRoleFailure(errorMessage));
    }
  };
};

// RBAC - Delete User
export const deleteUserStart = () => ({
  type: edgeDevicesActionTypes.DELETE_USER_START
});

export const deleteUserSuccess = (userId) => ({
  type: edgeDevicesActionTypes.DELETE_USER_SUCCESS,
  payload: userId
});

export const deleteUserFailure = (error) => ({
  type: edgeDevicesActionTypes.DELETE_USER_FAILURE,
  payload: error
});

export const deleteUserAsync = (userId, token, clusterId, targetUserId) => {
  return async (dispatch) => {
    dispatch(deleteUserStart());
    
    try {
      await axiosInstance.delete(`${API_BASE}/users/${userId}/clusters/${clusterId}/rbac/users/${targetUserId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      dispatch(deleteUserSuccess(targetUserId));
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to delete user';
      dispatch(deleteUserFailure(errorMessage));
    }
  };
};

// RBAC - Fetch Roles
export const fetchRolesStart = () => ({
  type: edgeDevicesActionTypes.FETCH_ROLES_START
});

export const fetchRolesSuccess = (roles) => ({
  type: edgeDevicesActionTypes.FETCH_ROLES_SUCCESS,
  payload: roles
});

export const fetchRolesFailure = (error) => ({
  type: edgeDevicesActionTypes.FETCH_ROLES_FAILURE,
  payload: error
});

export const fetchRolesAsync = (userId, token, clusterId) => {
  return async (dispatch) => {
    dispatch(fetchRolesStart());
    
    try {
      const response = await axiosInstance.get(`${API_BASE}/users/${userId}/clusters/${clusterId}/rbac/roles`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      dispatch(fetchRolesSuccess(response.data));
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to fetch roles';
      dispatch(fetchRolesFailure(errorMessage));
    }
  };
};

// ============================================================================
// DEVICE REGISTRATION MANAGEMENT ACTIONS (Admin Only)
// ============================================================================

// Fetch pending devices actions
export const fetchPendingDevicesStart = () => ({
  type: edgeDevicesActionTypes.FETCH_PENDING_DEVICES_START
});

export const fetchPendingDevicesSuccess = (devices) => ({
  type: edgeDevicesActionTypes.FETCH_PENDING_DEVICES_SUCCESS,
  payload: devices
});

export const fetchPendingDevicesFailure = (error) => ({
  type: edgeDevicesActionTypes.FETCH_PENDING_DEVICES_FAILURE,
  payload: error
});

export const fetchPendingDevicesStartAsync = (userId, token) => {
  return async (dispatch) => {
    dispatch(fetchPendingDevicesStart());
    
    try {
      const response = await axiosInstance.get(`${API_BASE}/users/${userId}/devices/pending`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Handle various response formats gracefully
      const devices = response.data?.devices || response.data || [];
      
      // Ensure we always dispatch an array, even if empty
      dispatch(fetchPendingDevicesSuccess(Array.isArray(devices) ? devices : []));
    } catch (error) {
      // Handle different types of errors
      let errorMessage = 'Failed to fetch pending devices';
      
      if (error.response) {
        // Server responded with error status
        const status = error.response.status;
        const serverMessage = error.response.data?.message || error.response.data?.error;
        
        if (status === 404) {
          // No pending devices found - this is normal
          dispatch(fetchPendingDevicesSuccess([]));
          return;
        } else if (status === 401) {
          errorMessage = 'Authentication failed. Please log in again.';
        } else if (status === 403) {
          // User is not admin - fail silently for non-admin users
          dispatch(fetchPendingDevicesSuccess([]));
          return;
        } else if (status === 500) {
          errorMessage = 'Server error. Please try again later.';
        } else if (serverMessage) {
          errorMessage = serverMessage;
        }
      } else if (error.request) {
        // Network error
        errorMessage = 'Network error. Please check your connection and try again.';
      } else {
        // Other error
        errorMessage = error.message || errorMessage;
      }
      
      dispatch(fetchPendingDevicesFailure(errorMessage));
    }
  };
};

// Approve device registration actions
export const approveDeviceStart = () => ({
  type: edgeDevicesActionTypes.APPROVE_DEVICE_START
});

export const approveDeviceSuccess = (device) => ({
  type: edgeDevicesActionTypes.APPROVE_DEVICE_SUCCESS,
  payload: device
});

export const approveDeviceFailure = (error) => ({
  type: edgeDevicesActionTypes.APPROVE_DEVICE_FAILURE,
  payload: error
});

export const approveDeviceStartAsync = (userId, token, nodeId, clusterId = null) => {
  return async (dispatch) => {
    dispatch(approveDeviceStart());
    
    try {
      const requestBody = { status: 'approved' };
      if (clusterId) {
        requestBody.clusterId = clusterId;
      }
      
      const response = await axiosInstance.patch(`${API_BASE}/admin/nodes/${nodeId}/approve`, requestBody, {
        headers: { Authorization: `Bearer ${token}` }
      });
      dispatch(approveDeviceSuccess(response.data));
      
      // Refresh pending devices list
      dispatch(fetchPendingDevicesStartAsync(userId, token));
      
      // If assigned to cluster, refresh nodes for that cluster
      if (clusterId) {
        dispatch(fetchNodesStartAsync(userId, token, clusterId));
      } else {
        // Refresh unassigned nodes
        dispatch(fetchUnassignedNodesStartAsync(userId, token));
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to approve device';
      dispatch(approveDeviceFailure(errorMessage));
    }
  };
};

// Reject device registration actions
export const rejectDeviceStart = () => ({
  type: edgeDevicesActionTypes.REJECT_DEVICE_START
});

export const rejectDeviceSuccess = (nodeId) => ({
  type: edgeDevicesActionTypes.REJECT_DEVICE_SUCCESS,
  payload: nodeId
});

export const rejectDeviceFailure = (error) => ({
  type: edgeDevicesActionTypes.REJECT_DEVICE_FAILURE,
  payload: error
});

export const rejectDeviceStartAsync = (userId, token, nodeId, reason = '') => {
  return async (dispatch) => {
    dispatch(rejectDeviceStart());
    
    try {
      await axiosInstance.delete(`${API_BASE}/admin/nodes/${nodeId}/reject`, {
        headers: { Authorization: `Bearer ${token}` },
        data: { reason }
      });
      dispatch(rejectDeviceSuccess(nodeId));
      
      // Refresh pending devices list
      dispatch(fetchPendingDevicesStartAsync(userId, token));
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to reject device';
      dispatch(rejectDeviceFailure(errorMessage));
    }
  };
}; 